<template>
  <div>
    <div class="col-12 px-0">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 class="font-weight-bold mb-1">Parametrização do RMA</h5>
          <small class="text-muted">
            Configure quais tipos serão contabilizados em cada item do RMA.
          </small>
        </div>
      </div>
    </div>

    <div class="alert alert-info">
      <strong>Atenção!</strong>
      Os itens marcados como <b>fixos</b> são obrigatórios e não podem ser removidos.
      Os demais tipos podem ser adicionados ou removidos conforme a necessidade do município.
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-body py-3">
        <div class="row">
          <div class="col-md-3">
            <label>Relatório</label>
            <b-form-select
              v-model="filtros.relatorio"
              :options="relatorioOptions"
              @change="getItens"
            />
          </div>

          <div class="col-md-3">
            <label>Fonte</label>
            <b-form-select
              v-model="filtros.fonte"
              :options="fonteOptions"
              @change="getItens"
            />
          </div>

          <div class="col-md-6">
            <label>Pesquisar item</label>
            <div class="input-group">
              <b-input
                v-model="filtros.search"
                placeholder="Ex: C.1, visitas domiciliares, Cadastro Único..."
                @keyup.enter="getItens"
              />
              <div class="input-group-append">
                <button class="btn btn-primary" @click="getItens">
                  <i class="fas fa-search" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Lista de itens -->
      <div class="col-md-4">
        <div class="card shadow-sm rma-itens-card">
          <div class="card-header bg-white rma-itens-header">
            <strong>Itens do RMA</strong>
          </div>

          <div class="card-body p-0 rma-itens-scroll">
            <loading-animation
              :active.sync="loadingItens"
              :is-full-page="false"
              :opacity="1"
              color="#5895FF"
            />

            <div v-if="!loadingItens && itens.length === 0" class="text-center p-4">
              <img
                src="/img/icons/robot.svg"
                style="max-height: 120px"
                class="mb-3"
              />
              <p class="mb-0 font-weight-bold">Nenhum item encontrado</p>
            </div>

            <div v-for="grupo in itensAgrupados" :key="grupo.bloco">
              <div class="px-3 py-2 bg-light border-top border-bottom">
                <small class="font-weight-bold text-muted">
                  {{ grupo.bloco || "Sem bloco" }}
                </small>
              </div>

              <b-list-group flush>
                <b-list-group-item
                  v-for="item in grupo.itens"
                  :key="item.id"
                  button
                  @click="selecionarItem(item)"
                  :active="itemSelecionado && itemSelecionado.id === item.id"
                  class="py-2"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{{ item.codigo }}</strong>
                      <div class="small">{{ item.titulo }}</div>
                    </div>

                    <b-badge
                      v-if="item.permite_configuracao"
                      variant="success"
                    >
                      Config.
                    </b-badge>

                    <b-badge
                      v-else
                      variant="secondary"
                    >
                      Fixo
                    </b-badge>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
        </div>
      </div>

      <!-- Configuração do item -->
      <div class="col-md-8">
        <div class="card shadow-sm" v-if="!itemSelecionado">
          <div class="card-body text-center py-5">
            <img
              src="/img/icons/robot.svg"
              style="max-height: 140px"
              class="mb-4"
            />
            <h5 class="font-weight-bold">Selecione um item do RMA</h5>
            <p class="text-muted mb-0">
              Escolha um item na lista ao lado para visualizar ou alterar seus tipos contabilizados.
            </p>
          </div>
        </div>

        <div class="card shadow-sm" v-else>
          <div class="card-header bg-white">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h5 class="font-weight-bold mb-1">
                  {{ itemSelecionado.codigo }} - {{ itemSelecionado.titulo }}
                </h5>
<!-- 
                <div class="text-muted small">
                  Fonte principal:
                  <strong>{{ labelFonte(itemSelecionado.fonte_principal) }}</strong>
                  |
                  Estratégia:
                  <strong>{{ itemSelecionado.estrategia_calculo }}</strong>
                </div> -->
              </div>

              <b-badge
                :variant="itemSelecionado.permite_configuracao ? 'success' : 'secondary'"
              >
                {{ itemSelecionado.permite_configuracao ? "Configurável" : "Não configurável" }}
              </b-badge>
            </div>
          </div>

          <div class="card-body">
            <loading-animation
              :active.sync="loadingConfiguracao"
              :is-full-page="false"
              :opacity="1"
              color="#5895FF"
            />

            <div v-if="itemSelecionado.permite_configuracao">
              <div class="row align-items-end mb-3">
                <div class="col-md-4">
                  <label>Fonte</label>
                  <b-form-select
                    v-model="novo.fonte_tipo"
                    :options="fontesPermitidasOptions"
                    @change="getOpcoesFonte"
                  />
                </div>

                <div class="col-md-6">
                  <label>Tipo a adicionar</label>
                  <b-form-select
                    v-model="novo.fonte_id"
                    :options="opcoesFonteOptions"
                    :disabled="loadingOpcoesFonte || !novo.fonte_tipo"
                  />
                </div>

                <div class="col-md-2">
                  <button
                    class="btn btn-success btn-block btn-sm"
                    @click="adicionarMapeamento"
                    :disabled="!podeAdicionar"
                  >
                    <i class="fas fa-plus" />
                    Adicionar
                  </button>
                </div>
              </div>
            </div>

            <b-table
              :fields="fieldsMapeamentos"
              :items="mapeamentos"
              responsive
              striped
              hover
              small
              outlined
              show-empty
              table-class="mb-3"
            >
              <template v-slot:cell(fonte_descricao)="data">
                <strong>{{ data.item.fonte_descricao }}</strong>
                <div class="small text-muted">
                  {{ data.item.fonte_tipo_label }}
                </div>
              </template>

              <template v-slot:cell(origem)="data">
                <b-badge
                  :variant="data.item.origem === 'federal' ? 'primary' : 'success'"
                >
                  {{ data.item.origem.toUpperCase() }}
                </b-badge>
              </template>

              <template v-slot:cell(obrigatorio)="data">
                <b-badge
                  :variant="data.item.obrigatorio ? 'danger' : 'secondary'"
                >
                  {{ data.item.obrigatorio ? "Fixo" : "Opcional" }}
                </b-badge>
              </template>

              <template v-slot:cell(habilitado)="data">
                <b-form-checkbox
                  switch
                  v-model="data.item.habilitado"
                  :disabled="data.item.obrigatorio"
                >
                  {{ data.item.habilitado ? "Sim" : "Não" }}
                </b-form-checkbox>
              </template>

              <template v-slot:cell(actions)="data">
                <b-button
                  v-if="!data.item.obrigatorio"
                  variant="danger"
                  size="xs"
                  class="px-2"
                  v-b-tooltip.hover
                  title="Remover"
                  @click="removerMapeamento(data.item)"
                >
                  <i class="mdi mdi-delete" />
                </b-button>

                <!-- <b-button
                  v-else
                  variant="secondary"
                  size="xs"
                  class="px-2"
                  disabled
                  v-b-tooltip.hover
                  title="Tipo fixo"
                >
                  <i class="fas fa-lock" />
                </b-button> -->
              </template>

              <template v-slot:empty>
                <div class="text-center my-4">
                  <img
                    src="/img/icons/robot.svg"
                    style="max-height: 120px"
                    class="mb-3"
                  />
                  <h5 class="bold">Nenhum tipo configurado</h5>
                </div>
              </template>
            </b-table>

            <div class="d-flex justify-content-end">
              <button
                class="btn btn-light mr-2"
                @click="recarregarConfiguracao"
              >
                Cancelar
              </button>

              <button
                class="btn btn-success"
                @click="salvarMapeamentos"
                :disabled="!itemSelecionado"
              >
                <i class="fas fa-save" />
                Salvar alterações
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "RmaParametrizacao",

  data() {
    return {
      relatorios: [],
      fontes: [],

      filtros: {
        relatorio: "cras",
        fonte: null,
        search: "",
      },

      itens: [],
      itemSelecionado: null,

      mapeamentos: [],
      fontesPermitidas: [],
      opcoesFonte: [],

      novo: {
        fonte_tipo: null,
        fonte_id: null,
      },

      loadingItens: false,
      loadingConfiguracao: false,
      loadingOpcoesFonte: false,

      fieldsMapeamentos: [
        {
          key: "fonte_descricao",
          label: "Tipo",
        },
        {
          key: "origem",
          label: "Origem",
        },
        {
          key: "habilitado",
          label: "Contabilizar",
        },
        {
          key: "actions",
          label: "",
          class: "text-right",
          thStyle: { width: "90px", minWidth: "90px" },
        },
      ],
    };
  },

  computed: {
    relatorioOptions() {
      return this.relatorios.map((item) => ({
        value: item.codigo,
        text: item.nome,
      }));
    },

    fonteOptions() {
      const options = [
        {
          value: null,
          text: "Todas",
        },
      ];

      this.fontes.forEach((fonte) => {
        options.push({
          value: fonte.codigo,
          text: fonte.nome,
        });
      });

      return options;
    },

    fontesPermitidasOptions() {
      return this.fontesPermitidas.map((fonte) => ({
        value: fonte.codigo,
        text: fonte.nome,
      }));
    },

    opcoesFonteOptions() {
      const options = [
        {
          value: null,
          text: this.loadingOpcoesFonte ? "Carregando..." : "Selecione",
        },
      ];

      this.opcoesFonte.forEach((item) => {
        options.push({
          value: item.id,
          text: item.descricao,
        });
      });

      return options;
    },

    itensAgrupados() {
      const grupos = {};

      this.itens.forEach((item) => {
        const bloco = item.bloco || "Sem bloco";

        if (!grupos[bloco]) {
          grupos[bloco] = [];
        }

        grupos[bloco].push(item);
      });

      return Object.keys(grupos).map((bloco) => ({
        bloco,
        itens: grupos[bloco],
      }));
    },

    podeAdicionar() {
      return (
        this.itemSelecionado &&
        this.itemSelecionado.permite_configuracao &&
        this.novo.fonte_tipo &&
        this.novo.fonte_id
      );
    },
  },

  mounted() {
    this.getRelatorios();
    this.getFontes();
    this.getItens();
  },

  methods: {
    getRelatorios() {
      axios.get("api/dashboard/admin/rma/relatorios").then((response) => {
        this.relatorios = response.data.data;
      });
    },

    getFontes() {
      axios.get("api/dashboard/admin/rma/fontes").then((response) => {
        this.fontes = response.data.data;
      });
    },

    getItens() {
      this.loadingItens = true;
      this.itemSelecionado = null;
      this.mapeamentos = [];

      axios
        .get(`api/dashboard/admin/rma/relatorios/${this.filtros.relatorio}/itens`, {
          params: {
            fonte: this.filtros.fonte,
            search: this.filtros.search,
          },
        })
        .then((response) => {
          this.itens = response.data.data;

          if (this.itens.length > 0) {
            this.selecionarItem(this.itens[0]);
          }
        })
        .finally(() => {
          this.loadingItens = false;
        });
    },

    selecionarItem(item) {
      this.itemSelecionado = item;
      this.recarregarConfiguracao();
    },

    recarregarConfiguracao() {
      if (!this.itemSelecionado) {
        return;
      }

      this.loadingConfiguracao = true;

      axios
        .get(`api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/configuracao`)
        .then((response) => {
          const data = response.data.data;

          this.itemSelecionado = data.item;
          this.mapeamentos = data.mapeamentos;
          this.fontesPermitidas = data.fontes_permitidas;

          if (this.fontesPermitidas.length > 0) {
            this.novo.fonte_tipo = this.fontesPermitidas[0].codigo;
            this.getOpcoesFonte();
          } else {
            this.novo.fonte_tipo = null;
            this.novo.fonte_id = null;
            this.opcoesFonte = [];
          }
        })
        .finally(() => {
          this.loadingConfiguracao = false;
        });
    },

    getOpcoesFonte() {
      this.novo.fonte_id = null;
      this.opcoesFonte = [];

      if (!this.itemSelecionado || !this.novo.fonte_tipo) {
        return;
      }

      this.loadingOpcoesFonte = true;

      axios
        .get(`api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/opcoes-fonte`, {
          params: {
            fonte_tipo: this.novo.fonte_tipo,
          },
        })
        .then((response) => {
          this.opcoesFonte = response.data.data;
        })
        .finally(() => {
          this.loadingOpcoesFonte = false;
        });
    },

    adicionarMapeamento() {
      if (!this.podeAdicionar) {
        return;
      }

      axios
        .post(`api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/mapeamentos`, {
          fonte_tipo: this.novo.fonte_tipo,
          fonte_id: this.novo.fonte_id,
        })
        .then((response) => {
          this.$snack.success({
            button: false,
            text: "Tipo adicionado ao item do RMA com sucesso.",
          });

          this.novo.fonte_id = null;
          this.recarregarConfiguracao();
        });
    },

    salvarMapeamentos() {
      if (!this.itemSelecionado) {
        return;
      }

      axios
        .post(`api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/mapeamentos/salvar`, {
          mapeamentos: this.mapeamentos.map((item) => ({
            id: item.id,
            habilitado: item.habilitado,
          })),
        })
        .then(() => {
          this.$snack.success({
            button: false,
            text: "Parametrização do RMA salva com sucesso.",
          });

          this.recarregarConfiguracao();
        });
    },

    removerMapeamento(item) {
      this.$bvModal
        .msgBoxConfirm(
          `Deseja remover "${item.fonte_descricao}" deste item do RMA?`,
          {
            title: "Confirmar remoção",
            okTitle: "Remover",
            cancelTitle: "Cancelar",
            okVariant: "danger",
            cancelVariant: "light",
          }
        )
        .then((confirmado) => {
          if (!confirmado) {
            return;
          }

          axios
            .delete(`api/dashboard/admin/rma/mapeamentos/${item.id}`)
            .then(() => {
              this.$snack.success({
                button: false,
                text: "Tipo removido do item do RMA.",
              });

              this.recarregarConfiguracao();
            });
        });
    },

    labelFonte(codigo) {
      const fonte = this.fontes.find((item) => item.codigo === codigo);

      if (fonte) {
        return fonte.nome;
      }

      return codigo;
    },
  },
};
</script>

<style scoped>
.list-group-item.active {
  background-color: #2f80ed;
  border-color: #2f80ed;
}

.card-header {
  border-bottom: 1px solid #e9ecef;
}

.rma-itens-card {
  height: calc(100vh - 280px);
  min-height: 480px;
  display: flex;
  flex-direction: column;
}

.rma-itens-header {
  flex: 0 0 auto;
}

.rma-itens-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}
</style>